import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';

type HeroProps = {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  ctaLabel?: string;
  ctaHref?: string;
  priority?: boolean;
  overlay?: 'soft' | 'dark' | 'none';
  textTone?: 'light' | 'dark';
  textOutline?: 'none' | 'light' | 'dark';
};

export function Hero({ title, description, imageSrc, imageAlt, ctaLabel, ctaHref, priority, overlay = 'soft', textTone = 'light', textOutline = 'none' }: HeroProps) {
  const overlayClass =
    overlay === 'none'
      ? ''
      : overlay === 'dark'
      ? 'bg-gradient-to-r from-black/70 via-black/50 to-black/30'
      : 'bg-gradient-to-r from-black/40 via-black/25 to-black/10';
  const headingToneClass = textTone === 'dark' ? 'text-text-primary' : 'text-white';
  const descToneClass = textTone === 'dark' ? 'text-text-secondary' : 'text-white/90';
  const outlineClass = textOutline === 'light' ? 'text-outline-light' : textOutline === 'dark' ? 'text-outline-dark' : '';
  return (
    <section className="relative overflow-hidden rounded-3xl bg-neutral-900 text-white shadow-2xl">
      <div className="absolute inset-0">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          priority={priority}
          fetchPriority={priority ? 'high' : 'auto'}
          sizes="100vw"
          className="object-cover"
        />
        {overlay !== 'none' ? <div className={`absolute inset-0 ${overlayClass}`} /> : null}
      </div>
      <div className="container relative z-10 py-20 md:py-28 lg:py-32">
        <div className="max-w-3xl space-y-6 animate-fade-in">
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight drop-shadow-md ${headingToneClass} ${outlineClass}`}>
            {title}
          </h1>
          <p className={`text-lg md:text-xl lg:text-2xl leading-relaxed max-w-2xl drop-shadow ${descToneClass}`}>
            {description}
          </p>
          {ctaLabel && ctaHref ? (
            <div className="pt-4">
              <Link href={ctaHref}>
                <Button variant="primary" size="xl" className="min-h-[44px] min-w-[44px] shadow-xl">
                  {ctaLabel}
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
